import { useEffect, useState } from "react";
import { Phone, MessageSquare, Search, X } from "lucide-react";
import { SITE } from "@/lib/seoData";

const DISMISS_KEY = "ozy_sticky_cta_dismissed";

/**
 * Mobile-only sticky CTA bar at bottom of viewport.
 * Desktop gets a single floating pill.
 * Appears after ~3s or once the user scrolls a third of a viewport, and
 * remains dismissable for the browser session.
 */
export default function StickyCallBar({ onFreeAssessmentClick, searchMode = false }) {
  const Icon = searchMode ? Search : MessageSquare;
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem(DISMISS_KEY) === "1"
  );

  useEffect(() => {
    if (dismissed) return;
    let done = false;
    const show = () => {
      if (done) return;
      done = true;
      setVisible(true);
    };
    const onScroll = () => {
      if (window.scrollY > window.innerHeight * 0.35) show();
    };
    const t = setTimeout(show, 3000);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [dismissed]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <>
      {/* Mobile bottom bar */}
      {visible && (
        <div
          data-testid="sticky-mobile-cta"
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#050505]/95 backdrop-blur border-t border-zinc-800 px-3 pt-2 pb-[calc(env(safe-area-inset-bottom,0)+8px)]"
        >
          <div className="grid grid-cols-[1fr_1fr_36px] gap-2 items-center">
            <a
              href={`tel:${SITE.phone}`}
              data-testid="sticky-cta-call"
              className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-zinc-700 text-white text-sm font-medium"
            >
              <Phone className="w-4 h-4" /> Call now
            </a>
            <button
              type="button"
              onClick={onFreeAssessmentClick}
              data-testid="sticky-cta-callback"
              className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#E50914] hover:bg-[#F6121D] text-white text-sm font-semibold"
            >
              <Icon className="w-4 h-4" /> {searchMode ? "Search Finance" : "Human review"}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="sticky-cta-dismiss"
              aria-label="Dismiss"
              className="h-9 w-9 rounded-full text-zinc-500 hover:text-zinc-300 inline-flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Desktop floating pill */}
      {visible && (
        <button
          data-testid="sticky-desktop-cta"
          type="button"
          onClick={onFreeAssessmentClick}
          className="hidden md:inline-flex fixed bottom-6 right-6 z-40 items-center gap-2 h-12 px-5 rounded-full bg-[#E50914] hover:bg-[#F6121D] text-white text-sm font-semibold shadow-[0_10px_30px_rgba(229,9,20,0.35)]"
        >
          <Icon className="w-4 h-4" /> {searchMode ? "Start your finance search" : "Request a Human Review"}
        </button>
      )}
    </>
  );
}
